import Box from '@mui/material/Box';
import { StateView } from '@alola/ui';
import { Suspense, lazy } from 'react';
import { Route, Routes, useLocation } from 'react-router';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { useSession } from './api/session';
import { useLocale } from './locale';
import { routePermissions } from './shell/navigation';

/**
 * Route-level code splitting.
 *
 * Every feature area is a separate chunk, so the first download is the shell and nothing else. A
 * collections officer never fetches the marketing screens, and adding a feature area costs the other
 * roles nothing — which is what keeps the bundle budget reachable as the product grows rather than
 * something to renegotiate.
 *
 * Each lazy page gets a **stable fallback** of the height the content will occupy, so the layout does
 * not jump, and a chunk that fails to arrive gets a retry rather than a blank page.
 */
/**
 * The page chunks, one loader each. `lazy` renders from them, and `preloadPages` fetches them ahead of
 * need — once before a test file, so a cold compile is not timed as if it were a render.
 */
export const PAGE_MODULES = {
  Dashboard: () => import('./pages/DashboardPage'),
  Leads: () => import('./pages/LeadsPage'),
  LeadDetail: () => import('./pages/LeadDetailPage'),
  Customers: () => import('./pages/CustomersPage'),
  CustomerDetail: () => import('./pages/CustomerDetailPage'),
  Projects: () => import('./pages/ProjectsPage'),
  ProjectDetail: () => import('./pages/ProjectDetailPage'),
  Units: () => import('./pages/UnitsPage'),
  UnitDetail: () => import('./pages/UnitDetailPage'),
  Holds: () => import('./pages/HoldsPage'),
  PlanTemplates: () => import('./pages/PlanTemplatesPage'),
  LeadDistribution: () => import('./pages/LeadDistributionPage'),
  Recycle: () => import('./pages/RecyclePage'),
  SalesTeam: () => import('./pages/SalesTeamPage'),
  FollowUps: () => import('./pages/FollowUpsPage'),
  SalesDeals: () => import('./pages/DealsPage'),
  SalesPerformance: () => import('./pages/SalesPerformancePage'),
  CrmSettings: () => import('./pages/CrmSettingsPage'),
  Commissions: () => import('./pages/CommissionsPage'),
  CommissionRules: () => import('./pages/CommissionRulesPage'),
  Reservations: () => import('./pages/ReservationsPage'),
  ReservationNew: () => import('./pages/ReservationNewPage'),
  ReservationDetail: () => import('./pages/ReservationDetailPage'),
  Quotations: () => import('./pages/QuotationsPage'),
  QuotationNew: () => import('./pages/QuotationNewPage'),
  QuotationDetail: () => import('./pages/QuotationDetailPage'),
  Contracts: () => import('./pages/ContractsPage'),
  ContractDetail: () => import('./pages/ContractDetailPage'),
  Installments: () => import('./pages/InstallmentsPage'),
  Receipts: () => import('./pages/ReceiptsPage'),
  ReceiptDetail: () => import('./pages/ReceiptDetailPage'),
  Instruments: () => import('./pages/InstrumentsPage'),
  Reminders: () => import('./pages/RemindersPage'),
  Campaigns: () => import('./pages/CampaignsPage'),
  CampaignDetail: () => import('./pages/CampaignDetailPage'),
  MarketingReport: () => import('./pages/MarketingReportPage'),
  Vendors: () => import('./pages/ProcurementVendorsPage'),
  PurchaseRequests: () => import('./pages/PurchaseRequestsPage'),
  PurchaseRequestDetail: () => import('./pages/PurchaseRequestDetailPage'),
  PurchaseOrders: () => import('./pages/PurchaseOrdersPage'),
  PurchaseOrderDetail: () => import('./pages/PurchaseOrderDetailPage'),
  VendorInvoices: () => import('./pages/VendorInvoicesPage'),
  VendorInvoiceDetail: () => import('./pages/VendorInvoiceDetailPage'),
  Payables: () => import('./pages/PayablesPage'),
  Organization: () => import('./pages/OrganizationPage'),
  Notifications: () => import('./pages/NotificationsPage'),
  Tasks: () => import('./pages/TasksPage'),
  Approvals: () => import('./pages/ApprovalsPage'),
  Imports: () => import('./pages/ImportsPage'),
  Users: () => import('./pages/UsersPage'),
  Audit: () => import('./pages/AuditPage'),
  Account: () => import('./pages/AccountPage'),
  CompanyIdentity: () => import('./pages/CompanyIdentityPage'),
  SalesSettings: () => import('./pages/SalesSettingsPage'),
  ReferenceData: () => import('./pages/ReferenceDataPage'),
  SalesReport: () => import('./pages/SalesReportPage'),
  UnitCompare: () => import('./pages/unit-views'),
  Readiness: () => import('./pages/ReadinessPage'),
  ApprovalPolicies: () => import('./pages/ApprovalPoliciesPage'),
  OpeningBalances: () => import('./pages/OpeningBalancesPage'),
  Onboarding: () => import('./pages/OnboardingPage'),
  FinanceAccounts: () => import('./pages/FinanceAccountsPage'),
  FinancePeriods: () => import('./pages/FinancePeriodsPage'),
  Budgets: () => import('./pages/BudgetsPage'),
  Assets: () => import('./pages/AssetsPage'),
  Hr: () => import('./pages/HrPage'),
  Payroll: () => import('./pages/PayrollPage'),
  Expenses: () => import('./pages/ExpensesPage'),
  CashAdvances: () => import('./pages/CashAdvancesPage'),
  ExpenseTemplates: () => import('./pages/ExpenseTemplatesPage'),
  DocumentTemplates: () => import('./pages/DocumentTemplatesPage'),
  DataReset: () => import('./pages/DataResetPage'),
  ChequeLayouts: () => import('./pages/ChequeLayoutsPage'),
  MyRequests: () => import('./pages/MyRequestsPage'),
  ExpenseDetail: () => import('./pages/ExpenseDetailPage'),
  Handover: () => import('./pages/HandoverPage'),
  HandoverCase: () => import('./pages/HandoverCasePage'),
  MyPayslips: () => import('./pages/MyPayslipsPage'),
  EmployeeDetail: () => import('./pages/EmployeeDetailPage'),
  AssetDetail: () => import('./pages/AssetDetailPage'),
  Stores: () => import('./pages/StoresPage'),
  Construction: () => import('./pages/ConstructionPage'),
  ConstructionContract: () => import('./pages/ConstructionContractPage'),
  BudgetDetail: () => import('./pages/BudgetDetailPage'),
  JournalEntries: () => import('./pages/JournalEntriesPage'),
  JournalEntryNew: () => import('./pages/JournalEntryNewPage'),
  JournalEntryDetail: () => import('./pages/JournalEntryDetailPage'),
  PostingRules: () => import('./pages/PostingRulesPage'),
  FinanceReports: () => import('./pages/FinanceReportsPage'),
  FinanceDashboard: () => import('./pages/FinanceDashboardPage'),
  FinancialStatements: () => import('./pages/FinancialStatementsPage'),
  RevenueRecognition: () => import('./pages/RevenueRecognitionPage'),
  ContractCollections: () => import('./pages/ContractCollectionsPage'),
  ReceivablesReports: () => import('./pages/ReceivablesReportsPage'),
  Refunds: () => import('./pages/RefundsPage'),
  Invoices: () => import('./pages/InvoicesPage'),
  InvoiceNew: () => import('./pages/InvoiceNewPage'),
  InvoiceDetail: () => import('./pages/InvoiceDetailPage'),
  TreasuryPosition: () => import('./pages/TreasuryPositionPage'),
  TreasuryAccounts: () => import('./pages/TreasuryAccountsPage'),
  TreasuryAccountDetail: () => import('./pages/TreasuryAccountDetailPage'),
  TreasuryTransfers: () => import('./pages/TreasuryTransfersPage'),
  TreasuryPayments: () => import('./pages/TreasuryPaymentsPage'),
  TreasuryBanks: () => import('./pages/TreasuryBanksPage'),
  Reconciliations: () => import('./pages/ReconciliationsPage'),
  ReconciliationDetail: () => import('./pages/ReconciliationDetailPage'),
  NotFound: () => import('./pages/NotFoundPage'),
  Verify: () => import('./pages/VerifyPage'),
  Portal: () => import('./pages/portal/PortalApp'),
  SetPassword: () => import('./pages/SetPasswordPage'),
} as const;

export function preloadPages(): Promise<unknown[]> {
  return Promise.all(Object.values(PAGE_MODULES).map((load) => load()));
}

const Dashboard = lazy(PAGE_MODULES.Dashboard);
const Leads = lazy(PAGE_MODULES.Leads);
const LeadDetail = lazy(PAGE_MODULES.LeadDetail);
const Customers = lazy(PAGE_MODULES.Customers);
const CustomerDetail = lazy(PAGE_MODULES.CustomerDetail);
const Projects = lazy(PAGE_MODULES.Projects);
const ProjectDetail = lazy(PAGE_MODULES.ProjectDetail);
const Units = lazy(PAGE_MODULES.Units);
const Holds = lazy(PAGE_MODULES.Holds);
const PlanTemplates = lazy(PAGE_MODULES.PlanTemplates);
const LeadDistribution = lazy(PAGE_MODULES.LeadDistribution);
const Recycle = lazy(PAGE_MODULES.Recycle);
const SalesTeam = lazy(PAGE_MODULES.SalesTeam);
const FollowUps = lazy(PAGE_MODULES.FollowUps);
const SalesDeals = lazy(PAGE_MODULES.SalesDeals);
const SalesPerformance = lazy(PAGE_MODULES.SalesPerformance);
const CrmSettings = lazy(PAGE_MODULES.CrmSettings);
const Commissions = lazy(PAGE_MODULES.Commissions);
const CommissionRules = lazy(PAGE_MODULES.CommissionRules);
const UnitDetail = lazy(PAGE_MODULES.UnitDetail);
const Reservations = lazy(PAGE_MODULES.Reservations);
const ReservationNew = lazy(PAGE_MODULES.ReservationNew);
const ReservationDetail = lazy(PAGE_MODULES.ReservationDetail);
const Quotations = lazy(PAGE_MODULES.Quotations);
const QuotationNew = lazy(PAGE_MODULES.QuotationNew);
const QuotationDetail = lazy(PAGE_MODULES.QuotationDetail);
const Contracts = lazy(PAGE_MODULES.Contracts);
const ContractDetail = lazy(PAGE_MODULES.ContractDetail);
const Installments = lazy(PAGE_MODULES.Installments);
const Receipts = lazy(PAGE_MODULES.Receipts);
const ReceiptDetail = lazy(PAGE_MODULES.ReceiptDetail);
const Instruments = lazy(PAGE_MODULES.Instruments);
const Reminders = lazy(PAGE_MODULES.Reminders);
const Campaigns = lazy(PAGE_MODULES.Campaigns);
const CampaignDetail = lazy(PAGE_MODULES.CampaignDetail);
const MarketingReport = lazy(PAGE_MODULES.MarketingReport);
const Vendors = lazy(PAGE_MODULES.Vendors);
const PurchaseRequests = lazy(PAGE_MODULES.PurchaseRequests);
const PurchaseRequestDetail = lazy(PAGE_MODULES.PurchaseRequestDetail);
const PurchaseOrders = lazy(PAGE_MODULES.PurchaseOrders);
const PurchaseOrderDetail = lazy(PAGE_MODULES.PurchaseOrderDetail);
const VendorInvoices = lazy(PAGE_MODULES.VendorInvoices);
const VendorInvoiceDetail = lazy(PAGE_MODULES.VendorInvoiceDetail);
const Payables = lazy(PAGE_MODULES.Payables);
const Organization = lazy(PAGE_MODULES.Organization);
const Notifications = lazy(PAGE_MODULES.Notifications);
const Tasks = lazy(PAGE_MODULES.Tasks);
const Approvals = lazy(PAGE_MODULES.Approvals);
const Imports = lazy(PAGE_MODULES.Imports);
const Users = lazy(PAGE_MODULES.Users);
const Audit = lazy(PAGE_MODULES.Audit);
const Account = lazy(PAGE_MODULES.Account);
const CompanyIdentity = lazy(PAGE_MODULES.CompanyIdentity);
const SalesSettings = lazy(PAGE_MODULES.SalesSettings);
const ReferenceData = lazy(PAGE_MODULES.ReferenceData);
const SalesReport = lazy(PAGE_MODULES.SalesReport);
const UnitCompare = lazy(PAGE_MODULES.UnitCompare);
const Readiness = lazy(PAGE_MODULES.Readiness);
const ApprovalPolicies = lazy(PAGE_MODULES.ApprovalPolicies);
const OpeningBalances = lazy(PAGE_MODULES.OpeningBalances);
const Onboarding = lazy(PAGE_MODULES.Onboarding);
const FinanceAccounts = lazy(PAGE_MODULES.FinanceAccounts);
const FinancePeriods = lazy(PAGE_MODULES.FinancePeriods);
const Budgets = lazy(PAGE_MODULES.Budgets);
const Assets = lazy(PAGE_MODULES.Assets);
const Hr = lazy(PAGE_MODULES.Hr);
const Payroll = lazy(PAGE_MODULES.Payroll);
const Expenses = lazy(PAGE_MODULES.Expenses);
const CashAdvances = lazy(PAGE_MODULES.CashAdvances);
const ExpenseTemplates = lazy(PAGE_MODULES.ExpenseTemplates);
const DocumentTemplates = lazy(PAGE_MODULES.DocumentTemplates);
const DataReset = lazy(PAGE_MODULES.DataReset);
const ChequeLayouts = lazy(PAGE_MODULES.ChequeLayouts);
const MyRequests = lazy(PAGE_MODULES.MyRequests);
const ExpenseDetail = lazy(PAGE_MODULES.ExpenseDetail);
const Handover = lazy(PAGE_MODULES.Handover);
const HandoverCase = lazy(PAGE_MODULES.HandoverCase);
const MyPayslips = lazy(PAGE_MODULES.MyPayslips);
const EmployeeDetail = lazy(PAGE_MODULES.EmployeeDetail);
const AssetDetail = lazy(PAGE_MODULES.AssetDetail);
const Stores = lazy(PAGE_MODULES.Stores);
const Construction = lazy(PAGE_MODULES.Construction);
const ConstructionContract = lazy(PAGE_MODULES.ConstructionContract);
const BudgetDetail = lazy(PAGE_MODULES.BudgetDetail);
const JournalEntries = lazy(PAGE_MODULES.JournalEntries);
const JournalEntryNew = lazy(PAGE_MODULES.JournalEntryNew);
const JournalEntryDetail = lazy(PAGE_MODULES.JournalEntryDetail);
const PostingRules = lazy(PAGE_MODULES.PostingRules);
const FinanceReports = lazy(PAGE_MODULES.FinanceReports);
const FinancialStatements = lazy(PAGE_MODULES.FinancialStatements);
const RevenueRecognition = lazy(PAGE_MODULES.RevenueRecognition);
const FinanceDashboard = lazy(PAGE_MODULES.FinanceDashboard);
const ContractCollections = lazy(PAGE_MODULES.ContractCollections);
const ReceivablesReports = lazy(PAGE_MODULES.ReceivablesReports);
const Refunds = lazy(PAGE_MODULES.Refunds);
const Invoices = lazy(PAGE_MODULES.Invoices);
const InvoiceNew = lazy(PAGE_MODULES.InvoiceNew);
const InvoiceDetail = lazy(PAGE_MODULES.InvoiceDetail);
const TreasuryPosition = lazy(PAGE_MODULES.TreasuryPosition);
const TreasuryAccounts = lazy(PAGE_MODULES.TreasuryAccounts);
const TreasuryAccountDetail = lazy(PAGE_MODULES.TreasuryAccountDetail);
const TreasuryTransfers = lazy(PAGE_MODULES.TreasuryTransfers);
const TreasuryPayments = lazy(PAGE_MODULES.TreasuryPayments);
const TreasuryBanks = lazy(PAGE_MODULES.TreasuryBanks);
const Reconciliations = lazy(PAGE_MODULES.Reconciliations);
const ReconciliationDetail = lazy(PAGE_MODULES.ReconciliationDetail);
const NotFound = lazy(PAGE_MODULES.NotFound);

/** Tall enough that the page does not reflow when the chunk arrives. */
export function RouteFallback() {
  const { t } = useLocale();
  return (
    <Box
      sx={{ minHeight: 320, display: 'grid', alignContent: 'center' }}
      data-testid="route-fallback"
    >
      <StateView
        kind="loading"
        title={t('shell.loadingSection')}
        description={t('states.loadingDescription')}
      />
    </Box>
  );
}

export function AppRoutes() {
  const { t } = useLocale();
  const { canAny } = useSession();
  const location = useLocation();
  // The menu's rule for a typed or followed address (2026-10-26): a section that is not this
  // person's work never mounts, so its page never requests data the server would refuse anyway.
  const required = routePermissions(location.pathname);
  if (required && required.length > 0 && !canAny(required)) {
    return (
      <StateView
        kind="forbidden"
        heading="h1"
        title={t('states.forbiddenTitle')}
        description={t('states.forbiddenDescription')}
      />
    );
  }
  return (
    // Keyed by path so an error on one screen does not keep the next one from rendering.
    <RouteErrorBoundary
      key={location.pathname}
      title={t('states.errorTitle')}
      description={t('states.errorDescription')}
      retryLabel={t('states.retry')}
    >
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/leads" element={<Leads />} />
          <Route path="/leads/:leadId" element={<LeadDetail />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/customers/:customerId" element={<CustomerDetail />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/projects/:projectId" element={<ProjectDetail />} />
          <Route path="/units" element={<Units />} />
          <Route path="/units/:unitId" element={<UnitDetail />} />
          <Route path="/holds" element={<Holds />} />
          <Route path="/plan-templates" element={<PlanTemplates />} />
          <Route path="/settings/lead-distribution" element={<LeadDistribution />} />
          <Route path="/sales/recycle" element={<Recycle />} />
          <Route path="/sales/team" element={<SalesTeam />} />
          <Route path="/sales/follow-ups" element={<FollowUps />} />
          <Route path="/sales/deals" element={<SalesDeals />} />
          <Route path="/sales/performance" element={<SalesPerformance />} />
          <Route path="/settings/crm" element={<CrmSettings />} />
          <Route path="/commissions" element={<Commissions />} />
          <Route path="/settings/commission-rules" element={<CommissionRules />} />
          <Route path="/reservations" element={<Reservations />} />
          <Route path="/reservations/new" element={<ReservationNew />} />
          <Route path="/reservations/:reservationId" element={<ReservationDetail />} />
          <Route path="/quotations" element={<Quotations />} />
          <Route path="/quotations/new" element={<QuotationNew />} />
          <Route path="/quotations/:quotationId" element={<QuotationDetail />} />
          <Route path="/contracts" element={<Contracts />} />
          <Route path="/contracts/:contractId" element={<ContractDetail />} />
          <Route path="/installments" element={<Installments />} />
          <Route path="/receipts" element={<Receipts />} />
          <Route path="/receipts/:receiptId" element={<ReceiptDetail />} />
          <Route path="/instruments" element={<Instruments />} />
          <Route path="/reminders" element={<Reminders />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/campaigns/:campaignId" element={<CampaignDetail />} />
          <Route path="/reports/marketing" element={<MarketingReport />} />
          <Route path="/procurement/vendors" element={<Vendors />} />
          <Route path="/procurement/requests" element={<PurchaseRequests />} />
          <Route path="/procurement/requests/:requestId" element={<PurchaseRequestDetail />} />
          <Route path="/procurement/orders" element={<PurchaseOrders />} />
          <Route path="/procurement/orders/:orderId" element={<PurchaseOrderDetail />} />
          <Route path="/procurement/invoices" element={<VendorInvoices />} />
          <Route path="/procurement/invoices/:vendorInvoiceId" element={<VendorInvoiceDetail />} />
          <Route path="/procurement/payables" element={<Payables />} />
          <Route path="/organization" element={<Organization />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/tasks" element={<Tasks />} />
          <Route path="/approvals" element={<Approvals />} />
          <Route path="/imports" element={<Imports />} />
          <Route path="/admin/users" element={<Users />} />
          <Route path="/admin/audit" element={<Audit />} />
          <Route path="/account" element={<Account />} />
          <Route path="/settings/company" element={<CompanyIdentity />} />
          <Route path="/settings/sales" element={<SalesSettings />} />
          <Route path="/settings/reference-data" element={<ReferenceData />} />
          <Route path="/reports/sales" element={<SalesReport />} />
          <Route path="/units/compare" element={<UnitCompare />} />
          <Route path="/settings/readiness" element={<Readiness />} />
          <Route path="/settings/approval-policies" element={<ApprovalPolicies />} />
          <Route path="/finance/opening-balances" element={<OpeningBalances />} />
          <Route path="/finance/onboarding" element={<Onboarding />} />
          <Route path="/finance/accounts" element={<FinanceAccounts />} />
          <Route path="/finance/periods" element={<FinancePeriods />} />
          <Route path="/finance/budgets" element={<Budgets />} />
          <Route path="/finance/assets" element={<Assets />} />
          <Route path="/hr" element={<Hr />} />
          <Route path="/payroll" element={<Payroll />} />
          <Route path="/expenses" element={<Expenses />} />
          <Route path="/expenses/recurring" element={<ExpenseTemplates />} />
          <Route path="/finance/cash-advances" element={<CashAdvances />} />
          <Route path="/settings/templates" element={<DocumentTemplates />} />
          <Route path="/settings/data-reset" element={<DataReset />} />
          <Route path="/treasury/cheque-layouts" element={<ChequeLayouts />} />
          <Route path="/expenses/:expenseId" element={<ExpenseDetail />} />
          <Route path="/handover" element={<Handover />} />
          <Route path="/handover/cases/:caseId" element={<HandoverCase />} />
          <Route path="/my-payslips" element={<MyPayslips />} />
          <Route path="/my-requests" element={<MyRequests />} />
          <Route path="/hr/employees/:employeeId" element={<EmployeeDetail />} />
          <Route path="/finance/assets/:assetId" element={<AssetDetail />} />
          <Route path="/stores" element={<Stores />} />
          <Route path="/construction" element={<Construction />} />
          <Route path="/construction/contracts/:contractId" element={<ConstructionContract />} />
          <Route path="/finance/budgets/:budgetId" element={<BudgetDetail />} />
          <Route path="/finance/entries" element={<JournalEntries />} />
          <Route path="/finance/entries/new" element={<JournalEntryNew />} />
          <Route path="/finance/entries/:entryId" element={<JournalEntryDetail />} />
          <Route path="/finance/posting" element={<PostingRules />} />
          <Route path="/finance/reports" element={<FinanceReports />} />
          <Route path="/finance/statements" element={<FinancialStatements />} />
          <Route path="/finance/revenue-recognition" element={<RevenueRecognition />} />
          <Route path="/finance/dashboard" element={<FinanceDashboard />} />
          <Route path="/finance/invoices" element={<Invoices />} />
          <Route path="/finance/invoices/new" element={<InvoiceNew />} />
          <Route path="/finance/invoices/:invoiceId" element={<InvoiceDetail />} />
          <Route path="/contracts/:contractId/collections" element={<ContractCollections />} />
          <Route path="/collections/receivables" element={<ReceivablesReports />} />
          <Route path="/collections/refunds" element={<Refunds />} />
          <Route path="/treasury" element={<TreasuryPosition />} />
          <Route path="/treasury/accounts" element={<TreasuryAccounts />} />
          <Route path="/treasury/accounts/:accountId" element={<TreasuryAccountDetail />} />
          <Route path="/treasury/transfers" element={<TreasuryTransfers />} />
          <Route path="/treasury/payments" element={<TreasuryPayments />} />
          <Route path="/treasury/banks" element={<TreasuryBanks />} />
          <Route path="/treasury/reconciliation" element={<Reconciliations />} />
          <Route path="/treasury/reconciliation/:statementId" element={<ReconciliationDetail />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </RouteErrorBoundary>
  );
}
